import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import DictationInput from '../components/DictationInput';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Check, Clock, ChevronRight, Trophy, Target, Briefcase, Heart, Star, MessageCircle } from 'lucide-react';

const PHASE_ICONS = { 1: Heart, 2: Briefcase, 3: Target, 4: Star };
const PHASE_COLORS = { 1: 'teal', 2: 'sky', 3: 'amber', 4: 'violet' };

export default function RoadmapPage() {
  const { user, axiosAuth } = useAuth();
  const [roadmap, setRoadmap] = useState(null);
  const [expandedPhase, setExpandedPhase] = useState(1);
  const [loading, setLoading] = useState(true);
  const [nextStepsOpen, setNextStepsOpen] = useState(false);
  const [nextSteps, setNextSteps] = useState([]);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [evidenceTarget, setEvidenceTarget] = useState(null);
  const [evidenceNotes, setEvidenceNotes] = useState('');
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [evidenceUploading, setEvidenceUploading] = useState(false);
  const api = axiosAuth();

  const load = () => {
    api.get('/roadmap/progress').then(r => {
      setRoadmap(r.data);
      const firstIncomplete = r.data?.phases?.find(p => p.progress_percent < 100);
      if (firstIncomplete) setExpandedPhase(firstIncomplete.phase);
      const steps = (r.data?.phases || [])
        .map(p => p.milestones.find(m => !m.completed && !m.pending_approval))
        .filter(Boolean)
        .slice(0, 4);
      setNextSteps(steps);
      if (user.role === 'client' && steps.length > 0) {
        setNextStepsOpen(true);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const toggleMilestone = async (milestoneKey, currentStatus, pendingApproval, evidenceDocIds = [], evidenceNotesValue = '') => {
    if (pendingApproval) {
      return toast.info('This milestone is pending peer approval');
    }
    if (currentStatus && user.role === 'client') {
      return toast.info('Approved milestones cannot be unchecked');
    }
    try {
      await api.put(`/roadmap/milestone/${milestoneKey}`, { 
        completed: !currentStatus,
        completed_date: !currentStatus ? new Date().toISOString() : null,
        evidence_doc_ids: evidenceDocIds,
        evidence_notes: evidenceNotesValue
      });
      if (user.role === 'client' && !currentStatus) {
        toast.success('Submitted for peer approval!');
      } else {
        toast.success(!currentStatus ? 'Milestone completed!' : 'Milestone unchecked');
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update milestone');
    }
  };

  const handleMilestoneClick = (milestone) => {
    if (milestone.pending_approval) {
      return toast.info('This milestone is pending peer approval');
    }
    if (milestone.completed && user.role === 'client') {
      return toast.info('Approved milestones cannot be unchecked');
    }
    if (!milestone.completed) {
      setEvidenceTarget(milestone);
      setEvidenceNotes('');
      setEvidenceFile(null);
      setEvidenceOpen(true);
      return;
    }
    toggleMilestone(milestone.key, milestone.completed, milestone.pending_approval);
  };

  const handleEvidenceSubmit = async () => {
    if (!evidenceTarget) return;
    setEvidenceUploading(true);
    try {
      let docIds = [];
      if (evidenceFile) {
        const formData = new FormData();
        formData.append('file', evidenceFile);
        formData.append('title', `${evidenceTarget.title} Evidence`);
        formData.append('category', `roadmap_${evidenceTarget.key}`);
        const res = await api.post('/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (res?.data?.id) {
          docIds = [res.data.id];
        }
      }
      await toggleMilestone(evidenceTarget.key, false, false, docIds, evidenceNotes);
      setEvidenceOpen(false);
    } catch {
      toast.error('Unable to upload evidence');
    } finally {
      setEvidenceUploading(false);
    }
  };
  if (!roadmap) return null;

  return (
    <div className="space-y-6" data-testid="roadmap-page">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>My Reentry Roadmap</h1>
        <p className="text-muted-foreground text-sm mt-1">Your step-by-step journey to success</p>
      </div>

      {user.role === 'client' && (
        <div className="bg-sky-50 border border-sky-200 rounded-2xl p-3 text-sm text-sky-800">
          <p className="font-medium flex items-center gap-2"><MessageCircle size={14} /> Milestone completion requires peer approval</p>
          <p className="text-xs text-sky-600 mt-0.5">When you mark a step as done, your peer specialist will review and confirm it.</p>
        </div>
      )}

      {/* Overall Progress */}
      <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-br from-teal-50 to-sky-50">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-teal-600 flex items-center justify-center"><Trophy className="w-6 h-6 text-white" /></div>
              <div>
                <p className="text-sm text-teal-700 font-medium">Overall Progress</p>
                <p className="text-2xl font-bold text-teal-900">{roadmap.overall_progress}%</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-teal-600">{roadmap.total_completed} of {roadmap.total_milestones}</p>
              <p className="text-xs text-teal-600/70">milestones completed</p>
            </div>
          </div>
          <Progress value={roadmap.overall_progress} className="h-3 bg-teal-100" />
        </CardContent>
      </Card>

      {/* Phases */}
      <div className="space-y-3">
        {roadmap.phases.map(phase => {
          const Icon = PHASE_ICONS[phase.phase] || Target;
          const color = PHASE_COLORS[phase.phase] || 'teal';
          const isExpanded = expandedPhase === phase.phase;
          const isComplete = phase.progress_percent === 100;

          return (
            <Card key={phase.phase} className={`border-0 shadow-sm rounded-2xl overflow-hidden transition-all ${isComplete ? 'bg-emerald-50/50' : ''}`}>
              <button
                onClick={() => setExpandedPhase(isExpanded ? null : phase.phase)}
                className="w-full p-4 flex items-center gap-4 hover:bg-muted/30 transition-colors"
                data-testid={`phase-${phase.phase}-toggle`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${isComplete ? 'bg-emerald-100' : `bg-${color}-100`}`}>
                  {isComplete ? <Check className="w-6 h-6 text-emerald-600" /> : <Icon className={`w-6 h-6 text-${color}-600`} />}
                </div>
                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">Phase {phase.phase}</Badge>
                    {isComplete && <Badge className="bg-emerald-100 text-emerald-700 text-xs">Complete</Badge>}
                  </div>
                  <p className="font-semibold mt-0.5">{phase.name}</p>
                  <p className="text-xs text-muted-foreground">{phase.description}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className={`text-lg font-bold ${isComplete ? 'text-emerald-600' : 'text-foreground'}`}>{phase.progress_percent}%</p>
                    <p className="text-xs text-muted-foreground">{phase.completed_count}/{phase.total_count}</p>
                  </div>
                  <ChevronRight className={`w-5 h-5 text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                </div>
              </button>

              {isExpanded && (
                <div className="border-t p-4 space-y-2 bg-muted/20">
                  {phase.milestones.map(milestone => (
                    <button
                      key={milestone.key}
                      onClick={() => handleMilestoneClick(milestone)}
                      className={`w-full p-3 rounded-xl flex items-center gap-3 transition-all ${
                        milestone.completed 
                          ? 'bg-emerald-50 border border-emerald-200' 
                          : milestone.pending_approval
                            ? 'bg-amber-50 border border-amber-200'
                            : 'bg-white border border-transparent hover:border-muted-foreground/20'
                      }`}
                      data-testid={`milestone-${milestone.key}`}
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-all ${
                        milestone.completed ? 'bg-emerald-500 text-white' : milestone.pending_approval ? 'bg-amber-400 text-white' : 'border-2 border-muted-foreground/30'
                      }`}>
                        {milestone.completed && <Check size={14} />}
                        {milestone.pending_approval && <Clock size={12} />}
                      </div>
                      <div className="flex-1 text-left">
                        <p className={`font-medium text-sm ${milestone.completed ? 'text-emerald-800' : milestone.pending_approval ? 'text-amber-800' : ''}`}>
                          {milestone.title}
                        </p>
                        <p className="text-xs text-muted-foreground">{milestone.description}</p>
                        {milestone.rejection_comments && (
                          <p className="text-xs text-red-600 mt-1">Feedback: {milestone.rejection_comments}</p>
                        )}
                      </div>
                      {milestone.completed && milestone.completed_date && (
                        <span className="text-xs text-emerald-600">{new Date(milestone.completed_date).toLocaleDateString()}</span>
                      )}
                      {milestone.pending_approval && (
                        <Badge className="bg-amber-100 text-amber-700 text-xs gap-1"><Clock size={10} /> Pending</Badge>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-r from-violet-50 to-pink-50">
        <CardContent className="p-6 text-center">
          <p className="text-violet-800 font-medium">
            {roadmap.overall_progress === 100 
              ? "Congratulations! You've completed your reentry roadmap!" 
              : roadmap.overall_progress >= 50 
                ? "You're making incredible progress! Keep going!" 
                : "Every step forward counts. You've got this!"}
          </p>
        </CardContent>
      </Card>

      <Dialog open={nextStepsOpen} onOpenChange={setNextStepsOpen}>
        <DialogContent className="max-w-2xl" data-testid="roadmap-next-steps-dialog">
          <DialogHeader>
            <DialogTitle>Next Steps for Your Roadmap</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {nextSteps.length ? nextSteps.map(step => (
              <div key={step.key} className="rounded-xl border border-slate-200 p-3">
                <p className="text-sm font-semibold">{step.title}</p>
                <p className="text-xs text-muted-foreground">{step.description}</p>
              </div>
            )) : (
              <p className="text-sm text-muted-foreground">No next steps right now. Great work!</p>
            )}
            <Button variant="outline" className="rounded-full" onClick={() => setNextStepsOpen(false)} data-testid="roadmap-next-steps-close">Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={evidenceOpen} onOpenChange={setEvidenceOpen}>
        <DialogContent className="max-w-2xl" data-testid="roadmap-evidence-dialog">
          <DialogHeader>
            <DialogTitle>Upload Evidence for {evidenceTarget?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <p className="text-sm font-medium mb-1">Upload document (optional)</p>
              <Input type="file" onChange={e => setEvidenceFile(e.target.files?.[0] || null)} data-testid="roadmap-evidence-file" />
            </div>
            <div>
              <p className="text-sm font-medium mb-1">Notes (optional)</p>
              <DictationInput value={evidenceNotes} onChange={(val) => setEvidenceNotes(val)} placeholder="Add any details about this milestone" data-testid="roadmap-evidence-notes" showCleanup={false} />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button className="rounded-full" onClick={handleEvidenceSubmit} disabled={evidenceUploading} data-testid="roadmap-evidence-submit">
                {user.role === 'client' ? 'Submit for Approval' : 'Mark Complete'}
              </Button>
              <Button variant="outline" className="rounded-full" onClick={() => setEvidenceOpen(false)} data-testid="roadmap-evidence-cancel">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
